/**
 * Chrome shared by the IP Intel Report pages: the site's NewNavBar (with the
 * "IP Intel Report" item active and an optional right-hand action), the
 * site's NewFooter, and the same auth / legal modal stack the other public
 * pages carry. Everything renders inside `.ipr` so the scoped styles apply.
 *
 * Tracking: every nav, footer and auth control here sends an ipintel_* event
 * with the page it happened on. The footer's own links (LinkedIn, WhatsApp,
 * newsletter) live in NewHome and send only their generic events, so a
 * delegated listener on the footer adds the ipintel_* copy for those.
 */

import React, { useEffect, useRef, useState } from 'react';
import { NewNavBar, NavItem, NewFooter } from '../NewHome';
import LoginModal from '../LoginModal';
import SignUpModal from '../SignUpModal';
import { PrivacyPolicyModal } from '../PrivacyPolicyModal';
import { TermsOfServiceModal } from '../TermsOfServiceModal';
import { IpIntelStyles } from './styles';
import { currentIpIntelPage, ipClip, ipTrack } from './track';

interface Props {
  /** Rendered in the nav's right-hand area (desktop and mobile). */
  rightAction?: React.ReactNode;
  /** A page's own header in place of the site's NewNavBar (the IRCC page's
   *  co-branded one), so the page never shows two headers. */
  header?: React.ReactNode;
  /** What the active "IP Intel Report" nav/footer entry does on this page. */
  onSelf: () => void;
  children: React.ReactNode;
}

const go = (href: string) => { window.location.href = href; };

export const IpIntelShell: React.FC<Props> = ({ rightAction, header, onSelf, children }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const page = currentIpIntelPage();

  const nav = (item: string, then: () => void) => () => { ipTrack('ipintel_nav_click', { item, page }); then(); };
  const foot = (item: string, then: () => void) => () => { ipTrack('ipintel_footer_click', { item, page }); then(); };

  const navItems: NavItem[] = [
    { id: 'cosmos', label: 'Cosmos', href: '/', onClick: nav('cosmos', () => go('/')) },
    { id: 'about_us', label: 'About Us', href: '/about', onClick: nav('about_us', () => go('/about')) },
    { id: 'ip_intel_report', label: 'IP Intel Report', href: '/ip-intel-report', active: true, onClick: nav('ip_intel_report', onSelf) },
  ];

  // The footer's LinkedIn / WhatsApp / newsletter controls are NewHome's and
  // carry only generic events; add the ipintel_* copy by delegation. Capture
  // phase, so it runs before a target=_blank link hands focus to a new tab.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      const footer = t?.closest('footer');
      if (!t || !footer || !root.contains(footer)) return;
      const a = t.closest('a') as HTMLAnchorElement | null;
      if (a) {
        const href = a.getAttribute('href') || '';
        if (/wa\.me|whatsapp/i.test(href)) { ipTrack('ipintel_click_whatsapp', { page }); return; }
        if (/linkedin\.com/i.test(href)) { ipTrack('ipintel_footer_click', { item: 'linkedin', page }); return; }
        if (/^https?:\/\//i.test(href)) {
          let host = '';
          try { host = new URL(href).host; } catch { /* ignore */ }
          if (host && host !== window.location.host) ipTrack('ipintel_click_outbound', { host: ipClip(host, 80), where: 'footer', page });
        }
        return;
      }
      const b = t.closest('button');
      if (b && /subscribe/i.test(b.textContent || '')) ipTrack('ipintel_footer_click', { item: 'newsletter', page });
    };
    root.addEventListener('click', onClick, true);
    return () => root.removeEventListener('click', onClick, true);
  }, [page]);

  return (
    <div className="ipr" id="top" ref={rootRef}>
      <IpIntelStyles />
      {header ?? (
        <NewNavBar
          navItems={navItems}
          rightAction={rightAction}
          onLoginClick={() => { ipTrack('ipintel_nav_login', { page }); setShowLogin(true); }}
          onLogoClick={nav('logo', () => go('/'))}
        />
      )}

      <main>{children}</main>

      <NewFooter
        onShowAboutUs={foot('about_us', () => go('/about'))}
        onShowPrivacy={foot('privacy', () => setShowPrivacy(true))}
        onShowTerms={foot('terms', () => setShowTerms(true))}
        onShowRdSymposium={foot('rd_symposium', () => go('/rd-symposium'))}
        onShowUniversities={foot('universities', () => go('/universities'))}
        onShowComet={foot('comet', () => go('/comet'))}
        onShowIpIntel={foot('ip_intel_report', onSelf)}
        ipIntelActive
      />

      {showPrivacy && <PrivacyPolicyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsOfServiceModal isOpen={showTerms} onClose={() => setShowTerms(false)} />}
      {showLogin && (
        <LoginModal
          onClose={() => { ipTrack('ipintel_login_close', { page }); setShowLogin(false); }}
          // Same hand-off as /about and /rd-symposium: LoginModal has stored
          // the session by the time this fires, and a hard load of "/" lets
          // MainWithClerk pick it up and open Cosmos signed in. Staying on
          // this page left the visitor signed in but still here (2026-09-27).
          onLogin={() => {
            ipTrack('ipintel_login_success', { page });
            setShowLogin(false);
            go('/');
          }}
          onSignUpClick={() => { ipTrack('ipintel_login_open_signup', { page }); setShowLogin(false); setShowSignUp(true); }}
          onForgotPasswordSuccess={() => ipTrack('ipintel_password_reset_done', { page })}
        />
      )}
      {showSignUp && (
        <SignUpModal
          onClose={() => { ipTrack('ipintel_signup_close', { page }); setShowSignUp(false); }}
          // A new account goes through onboarding on "/", as on every other public page.
          onSignupSuccess={() => { ipTrack('ipintel_signup_success', { page }); setShowSignUp(false); go('/'); }}
        />
      )}
    </div>
  );
};
